import { useState, useEffect, memo, useCallback, type CSSProperties } from 'react'
import { collectionsApi } from '../../lib/api.js'
import { useStore } from '../../store/index.js'
import type { Collection } from '../../types/domain.js'
import { CollectionModal } from './CollectionModal.js'
import { Spinner } from '../common/Spinner.js'
import './ProgramsView.css'

// ─── Props ────────────────────────────────────────────────────────────────────

interface CollectionsPanelProps {
  channelId:          string
  onSelectCollection: (col: Collection) => void
  canManage?:         boolean
  /** Collection dont la fiche est ouverte au centre, mise en évidence ici. */
  selectedCollectionId?: string | null
  /** Renommage/recoloration : la fiche ouverte doit suivre. */
  onCollectionChanged?: (col: Collection) => void
  /** Suppression : la fiche ouverte doit se fermer. */
  onCollectionDeleted?: (id: string) => void
}

// ─── Composant ────────────────────────────────────────────────────────────────

export const CollectionsPanel = memo(function CollectionsPanel({ channelId, onSelectCollection, canManage = true, selectedCollectionId = null, onCollectionChanged, onCollectionDeleted }: CollectionsPanelProps) {
  const programsUpdateSeq = useStore(state => state.programsUpdateSeq)
  const [collections, setCollections] = useState<Collection[]>([])
  const [loading,     setLoading]     = useState(true)
  const [showModal,   setShowModal]   = useState(false)
  const [editing,     setEditing]     = useState<Collection | undefined>(undefined)
  const [search,      setSearch]      = useState('')

  const [sortOrder,   setSortOrder]   = useState<'manual' | 'name-asc' | 'name-desc' | 'count-desc'>('manual')

  const reload = useCallback((silent = false) => {
    if (!silent) setLoading(true)
    collectionsApi.list({ channelId })
      .then(setCollections)
      .finally(() => setLoading(false))
  }, [channelId])

  useEffect(() => { reload(collections.length > 0) }, [reload, programsUpdateSeq])

  const handleCreated = useCallback((col: Collection) => {
    setCollections(prev => [...prev, col].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)))
    setShowModal(false)
  }, [])

  const handleUpdated = useCallback((col: Collection) => {
    setCollections(prev => prev.map(c => c.id === col.id ? col : c))
    setEditing(undefined)
    setShowModal(false)
    // La fiche ouverte au centre porte une copie de la collection : sans ce
    // rappel, elle garderait l'ancien nom après un renommage.
    onCollectionChanged?.(col)
  }, [onCollectionChanged])

  const handleDeleted = useCallback((id: string) => {
    setCollections(prev => prev.filter(c => c.id !== id))
    // Fermer la fiche d'une collection qui n'existe plus, sinon le centre
    // afficherait un contenu qu'aucun écran ne peut plus recharger.
    onCollectionDeleted?.(id)
  }, [onCollectionDeleted])

  const handleEdit = useCallback((col: Collection, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditing(col)
    setShowModal(true)
  }, [])

  const handleNew = useCallback(() => {
    setEditing(undefined)
    setShowModal(true)
  }, [])

  const filtered = collections.filter(c =>
    !search ||
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.description ?? '').toLowerCase().includes(search.toLowerCase())
  ).sort((a, b) => {
    if (sortOrder === 'name-asc') return a.name.localeCompare(b.name, 'fr')
    if (sortOrder === 'name-desc') return b.name.localeCompare(a.name, 'fr')
    if (sortOrder === 'count-desc') return (b._count?.programs ?? 0) - (a._count?.programs ?? 0)
    return a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'fr')
  })

  return (
    <div className="catalog-list">

      {/* Toolbar */}
      <div className="catalog-toolbar catalog-toolbar--stack">
        <div className="catalog-search-row">
          <input
            type="text"
            placeholder="Rechercher…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="tp-input catalog-search"
            aria-label="Rechercher une collection"
          />
          <button
            type="button"
            onClick={handleNew}
            disabled={!canManage}
            title={!canManage ? 'Permission de gestion des collections et séries requise' : undefined}
            className="tp-button tp-button--info"
          >
            + Nouvelle
          </button>
        </div>

        <div className="catalog-filter-dropdowns">
          <select
            className="tp-select catalog-filter-select"
            value={sortOrder}
            onChange={e => setSortOrder(e.target.value as any)}
            aria-label="Trier les collections"
          >
            <option value="manual">Ordre de tri manuel</option>
            <option value="name-asc">Nom (A-Z)</option>
            <option value="name-desc">Nom (Z-A)</option>
            <option value="count-desc">Nombre de programmes (décroissant)</option>
          </select>
        </div>
      </div>

      {/* Compteur */}
      <div className="catalog-count" role="status">
        {loading ? <><Spinner /> Chargement…</> : `${filtered.length} collection${filtered.length > 1 ? 's' : ''}`}
      </div>

      {/* Liste */}
      <div className="catalog-scroll">
        {loading ? null : filtered.length === 0 ? (
          <div className="catalog-empty">
            {collections.length === 0
              ? 'Aucune collection. Créez-en une pour regrouper vos programmes par thème.'
              : 'Aucun résultat'}
          </div>
        ) : (
          filtered.map(col => (
            <CollectionRow
              key={col.id}
              collection={col}
              active={col.id === selectedCollectionId}
              onClick={() => onSelectCollection(col)}
              onEdit={(e) => handleEdit(col, e)}
              onDeleted={() => handleDeleted(col.id)}
              canManage={canManage}
            />
          ))
        )}
      </div>

      {showModal && canManage && (
        <CollectionModal
          channelId={channelId}
          collection={editing}
          onCreated={handleCreated}
          onUpdated={handleUpdated}
          onClose={() => { setShowModal(false); setEditing(undefined) }}
        />
      )}
    </div>
  )
})

// ─── CollectionRow ────────────────────────────────────────────────────────────

const CollectionRow = memo(function CollectionRow({ collection, onClick, onEdit, onDeleted, canManage, active }: {
  collection: Collection
  onClick:    () => void
  onEdit:     (e: React.MouseEvent) => void
  onDeleted:  () => void
  canManage:  boolean
  active:     boolean
}) {
  const accentStyle = collection.color
    ? { '--catalog-accent': collection.color } as CSSProperties
    : undefined
  const count  = collection._count?.programs ?? 0

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirm(`Supprimer la collection "${collection.name}" ?\n\nLes programmes ne seront pas supprimés, ils seront juste retirés de la collection.`)) return
    try {
      await collectionsApi.delete(collection.id)
      onDeleted()
    } catch (err: any) {
      alert(err.message)
    }
  }

  return (
    <div className={`catalog-collection-row${active ? ' is-active' : ''}`}>
      <button
        type="button"
        className="catalog-collection-row__select"
        aria-current={active ? 'true' : undefined}
        onClick={onClick}
      >
        <span className="catalog-collection-row__dot" style={accentStyle} aria-hidden="true" />
        <span className="catalog-collection-row__copy">
          <span className="catalog-collection-row__title">{collection.name}</span>
          <span className="catalog-collection-row__meta">
            {count} programme{count !== 1 ? 's' : ''}
            {collection.description && ` · ${collection.description}`}
          </span>
        </span>
      </button>

      <div className="catalog-row-actions">
        <button
          type="button"
          onClick={onEdit}
          disabled={!canManage}
          title={canManage ? 'Modifier' : 'Permission de gestion des collections et séries requise'}
          aria-label={`Modifier ${collection.name}`}
          className="catalog-icon-action"
        >
          ✎
        </button>
        <button
          type="button"
          onClick={handleDelete}
          disabled={!canManage}
          title={canManage ? 'Supprimer' : 'Permission de gestion des collections et séries requise'}
          aria-label={`Supprimer ${collection.name}`}
          className="catalog-icon-action catalog-icon-action--danger"
        >
          ×
        </button>
      </div>
    </div>
  )
})
